import { ChevronDownStroke, XStroke } from 'assets/icons';
import { useGetContent } from 'contexts/Content';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import sanitizeHtml from 'sanitize-html';
import { ArticleType } from 'types/article';
import { PhaseType } from 'types/package';
import {
  StyledTop,
  StyledHero,
  SectionContentWrapper,
  StyledReadTime,
  StyledHeading,
  StyledDescription,
  StyledAuthor,
  StyledAnchorMobileListWrapper,
  StyledListHeader,
  StyledAnchorList,
  StyledListItem,
  StyledSectionList,
  StyledArticleContent,
  TopContentWrapper,
  StyledArticleHeader,
  StyledAnchorDesktopListWrapper,
  StyledTopNav,
  StyledNavTitleMobile,
  StyledCloseButton,
  StyledMenuTray,
  StyledContentListItem,
  StyledAnchorMobileListDropdownWrapper,
  StyledWrapper,
  StyledNavTitle,
  StyledAuthorContainer,
} from './styles';

const STICKY_HEADER_OFFSET = 85;

const AnchorList: React.FC<{
  content: ArticleType;
  phase: PhaseType;
  handleScroll: (i: number) => void;
  activeSection: number;
  isDropdownListItem?: boolean;
  isLightBG?: boolean;
}> = ({
  content,
  handleScroll,
  activeSection,
  isDropdownListItem,
  isLightBG,
  phase,
}) => {
  return (
    <>
      <StyledListHeader>
        {isDropdownListItem ? 'Jump to section: ' : 'In this article:'}
      </StyledListHeader>
      <StyledAnchorList>
        {content.articleSections.map((section, i) => (
          <StyledListItem
            key={i}
            onClick={() => handleScroll(i)}
            variant={phase}
            index={i}
            isActiveSection={i === activeSection}
            isDropdownListItem={isDropdownListItem}
            isLightBG={isLightBG}
          >
            {section.title}
          </StyledListItem>
        ))}
      </StyledAnchorList>
    </>
  );
};

export const ArticleDetails: React.FC<{
  article: ArticleType;
  phase: PhaseType;
  careID: string;
}> = ({ article, phase, careID }) => {
  const [activeSection, setActiveSection] = useState(-1);
  const [progress, setProgress] = useState(0);
  const scrollRef = useRef(new Array(article.articleSections.length));
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getActiveSectionIndex = () =>
    scrollRef.current?.some((element: HTMLLIElement, index) => {
      if (
        Math.floor(element?.getBoundingClientRect().top) -
          STICKY_HEADER_OFFSET >
        0
      ) {
        setActiveSection(index - 1);
        return true;
      } else {
        setActiveSection(index);
        return;
      }
    });

  const getProgress = () => {
    const calculatedProgress =
      (window.scrollY / document.body.scrollHeight) * 100;
    // setProgress(calculatedProgress);
  };

  const handleScroll = () => {
    getActiveSectionIndex();
    getProgress();
  };

  const handleScrollTo = (target: number) => {
    if (scrollRef.current)
      window.scrollTo({
        left: 0,
        top: scrollRef.current[target].offsetTop - STICKY_HEADER_OFFSET,
      });
  };

  const readingTimeEstimate = useMemo(() => {
    const textLength = sanitizeHtml(
      article.articleSections.reduce((acc, cur) => {
        return acc + cur.content;
      }, ''),
      { allowedTags: [] },
    ).split(/\W+/).length;
    // Average reading speed 250 wpm
    return Math.floor(textLength / 250);
  }, [article]);

  return (
    <StyledWrapper>
      <StyledTopNav
        variant={phase}
        progress={progress}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <StyledNavTitle>{article.title}</StyledNavTitle>
        <StyledNavTitleMobile menuOpen={menuOpen}>
          {article.title}
          <ChevronDownStroke color="white" />
        </StyledNavTitleMobile>
        <StyledCloseButton to={`/care/${careID}`} $menuOpen={menuOpen}>
          <XStroke color="white" />
        </StyledCloseButton>
        <AnimatePresence>
          {menuOpen && (
            <StyledMenuTray
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: 'spring', stiffness: 100 }}
            >
              <StyledAnchorMobileListDropdownWrapper>
                <AnchorList
                  phase={phase}
                  content={article}
                  handleScroll={handleScrollTo}
                  activeSection={activeSection}
                  isDropdownListItem
                />
              </StyledAnchorMobileListDropdownWrapper>
            </StyledMenuTray>
          )}
        </AnimatePresence>
      </StyledTopNav>
      <StyledTop>
        <StyledHero src={article.hero} alt={article.heroAlt} />
        <TopContentWrapper>
          <StyledReadTime>
            Read time {readingTimeEstimate} minutes
          </StyledReadTime>
          <StyledHeading>{article.title}</StyledHeading>
          <StyledDescription>{article.description}</StyledDescription>
          <StyledAuthorContainer>
            <StyledAuthor>Written By: {article.author}</StyledAuthor>
            {article.reviewedBy && (
              <StyledAuthor>Reviewed By: {article.reviewedBy}</StyledAuthor>
            )}
          </StyledAuthorContainer>
          <StyledAnchorMobileListWrapper>
            <AnchorList
              content={article}
              phase={phase}
              handleScroll={handleScrollTo}
              activeSection={activeSection}
            />
          </StyledAnchorMobileListWrapper>
        </TopContentWrapper>
      </StyledTop>
      <SectionContentWrapper>
        <StyledAnchorDesktopListWrapper variant={phase}>
          <AnchorList
            content={article}
            phase={phase}
            handleScroll={handleScrollTo}
            activeSection={activeSection}
            isLightBG
          />
        </StyledAnchorDesktopListWrapper>
        <StyledSectionList>
          {article.articleSections.map((section, i) => (
            <StyledContentListItem
              key={i}
              ref={ref => (scrollRef.current[i] = ref)}
              variant={phase}
              index={i}
            >
              <StyledArticleHeader>{section.title}</StyledArticleHeader>
              <StyledArticleContent
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(section.content, {
                    allowedTags: sanitizeHtml.defaults.allowedTags.concat([
                      'img',
                    ]),
                  }),
                }}
              />
            </StyledContentListItem>
          ))}
        </StyledSectionList>
      </SectionContentWrapper>
    </StyledWrapper>
  );
};

const Article = () => {
  const { care_id, article_id } = useParams();
  const content = useGetContent(care_id as string);
  if (!content) {
    return null;
  }
  const targetArticle = content?.articles.find(
    article => article.id === (article_id as string),
  );

  if (!targetArticle) {
    return null;
  }
  return (
    <ArticleDetails
      article={targetArticle}
      phase={content.phase}
      careID={care_id as string}
    />
  );
};

export default Article;
